import mixpanel from "mixpanel-browser";
import {
  googleAnalyticsInit,
  eventCustom as ga4Event,
  sendPageView as ga4PageView,
} from "./google-analytics";
import {
  initGoogleTagManager,
  trackEvent as gtmEvent,
  pushToDataLayer,
} from "./google-tag-manager";
import {
  REACT_APP_MIXPANEL_TOKEN,
  REACT_APP_GOOGLE_ANALYTICS_TOGGLE,
  REACT_APP_GOOGLE_ANALYTICS,
  REACT_APP_GTM_TOGGLE,
  REACT_APP_GTM_ID,
} from "../../config";

/**
 * Configuration from runtime config
 */
const mixpanel_token = REACT_APP_MIXPANEL_TOKEN || false;
const ga4_enabled = REACT_APP_GOOGLE_ANALYTICS_TOGGLE && !!REACT_APP_GOOGLE_ANALYTICS;
const gtm_enabled = REACT_APP_GTM_TOGGLE && !!REACT_APP_GTM_ID;

/**
 * Prints a styled message to the console for debugging
 * @param {string} event - Event name
 * @param {Object} props - Event properties
 */
function consoleOut(event, props = {}) {
  console.debug(
    `%c🛰  ${JSON.stringify(event) + " : " + JSON.stringify(props)}`,
    "background: #a0e9ff; color: #333; padding: 10px;",
  );
}

/**
 * Tracking wrapper class that provides access to all tracking services
 * Each service can be called individually with its specific requirements
 */
class TrackingService {
  constructor() {
    // Initialize all tracking services
    this.initializeServices();
  }

  /**
   * Initialize all tracking services based on environment configuration
   */
  initializeServices() {
    // Initialize Mixpanel
    if (mixpanel_token) {
      mixpanel.init(mixpanel_token);
      this.mixpanelEnabled = true;
    } else {
      this.mixpanelEnabled = false;
    }

    // GA4 and GTM are initialized separately in index.js
    this.ga4Enabled = ga4_enabled;
    this.gtmEnabled = gtm_enabled;
  }

  /**
   * Send event to Mixpanel
   * @param {string} event - Event name
   * @param {Object} props - Event properties specific to Mixpanel
   */
  mixpanel(event, props = {}) {
    if (this.mixpanelEnabled) {
      if (Object.keys(props).length > 0) {
        mixpanel.track(event, props);
      } else {
        mixpanel.track(event);
      }
    }
    // Always log in development
    if (process.env.NODE_ENV === "development") {
      consoleOut(`Mixpanel: ${event}`, props);
    }
  }

  /**
   * Send event to Google Analytics 4
   * @param {string} category - Event category
   * @param {string} action - Event action
   * @param {string} label - Event label (optional)
   * @param {number} value - Event value (optional)
   */
  ga4(category, action, label = null, value = null) {
    if (this.ga4Enabled) {
      ga4Event(category, action, label, value);
    }
    // Always log in development
    if (process.env.NODE_ENV === "development") {
      consoleOut(`GA4: ${category}/${action}`, { label, value });
    }
  }

  /**
   * Send event to Google Tag Manager
   * @param {string} eventName - GTM event name
   * @param {Object} eventData - Event data for GTM dataLayer
   */
  gtm(eventName, eventData = {}) {
    if (this.gtmEnabled) {
      gtmEvent(eventName, eventData);
    }
    // Always log in development
    if (process.env.NODE_ENV === "development") {
      consoleOut(`GTM: ${eventName}`, eventData);
    }
  }

  /**
   * Push raw data to GTM dataLayer
   * @param {Object} data - Data to push to dataLayer
   */
  gtmDataLayer(data) {
    if (this.gtmEnabled) {
      pushToDataLayer(data);
    }
    // Always log in development
    if (process.env.NODE_ENV === "development") {
      consoleOut("GTM DataLayer Push", data);
    }
  }

  /**
   * Log method for debugging - only outputs to console
   * @param {string} event - Event name
   * @param {Object} props - Event properties
   */
  log(event, props = {}) {
    consoleOut(event, props);
  }

  /**
   * Check if a specific tracking service is enabled
   * @returns {Object} Status of each tracking service
   */
  getStatus() {
    return {
      mixpanel: this.mixpanelEnabled,
      ga4: this.ga4Enabled,
      gtm: this.gtmEnabled,
    };
  }

  /**
   * Convenience method for common e-commerce events
   * You can call specific trackers as needed for each event type
   */

  /**
   * Track a pickup scheduled event
   * Example of how to use multiple trackers for important events
   */
  trackPickupScheduled() {
    // Send to Mixpanel with Mixpanel-specific properties
    this.mixpanel("Step 8: Logged: Pickup Scheduled Success");

    // Send to GA4 with GA4-specific format
    this.ga4("Pickup", "scheduled");

    // Send to GTM with GTM-specific format
    this.gtm("pickup_scheduled", {
      event_category: "Pickup",
      event_action: "scheduled",
    });
  }

  /**
   * Track a page view
   * @param {string} pagePath - Page path
   * @param {string} pageTitle - Page title
   */
  trackPageView(pagePath, pageTitle) {
    const path = pagePath || window.location.pathname;
    const title = pageTitle || document.title;

    // Send to Mixpanel with page view properties
    if (this.mixpanelEnabled) {
      this.mixpanel("Page View", {
        page_path: path,
        page_title: title,
        page_location: window.location.href,
        page_referrer: document.referrer,
      });
      // Also track the page in Mixpanel's specific page tracking
      if (mixpanel.track_pageview) {
        mixpanel.track_pageview(path);
      }
    }

    // Send to GA4 using proper pageview method
    if (this.ga4Enabled) {
      ga4PageView(path, title);
    }

    // Send to GTM as page_view event
    if (this.gtmEnabled) {
      this.gtm("page_view", {
        page_path: path,
        page_title: title,
        page_location: window.location.href,
      });
    }
  }
}

// Create and export a singleton instance
const Tracking = new TrackingService();

export default Tracking;
